/**
 * Cache global del catálogo de entidades con 2 modos canónicos:
 *
 *   - 'operativo' (default): GET /mis-entidades → solo entidades visibles
 *     para el CO activo (catálogo base shared + propias del CO + suscritas §32).
 *     Es el modo del cliente normal. Cambia al switch-CO.
 *
 *   - 'global': GET /docs/openapi.json x-aarpia-entidades → TODAS las entidades
 *     del organismo. Vista de exploración admin. No filtra por CO.
 *
 * Doctrina G.3.7 multi-CO REAL: el cliente PWA en modo operativo solo ve lo
 * que su CO Tenant puede consumir (§22 dualidad + §32 contratos). Vista global
 * solo para Provider raíz o exploración del catálogo público.
 */
import { create } from 'zustand'
import { api } from '@/lib/api'

export type CatalogMode = 'operativo' | 'global'

export type EntidadOrigen = 'catalogo' | 'propia' | 'suscripcion'

export interface EntidadCatalogo {
  nombre: string
  sub: string
  token: string
  estado: string
  version: string
  status: string
  origen?: EntidadOrigen
  origen_suscripcion?: boolean
  motor_reglas?: string
  href?: string
}

interface MisEntidadesItem {
  id: string
  nombre: string
  app: string | null
  dominio: string | null
  token: string
  estado_deseable: string
  status: string
  origen: EntidadOrigen
  origen_suscripcion?: boolean
  /** Versión real del definition (la expone /mis-entidades desde G-AC5). */
  version?: string
}

interface CatalogState {
  mode: CatalogMode
  entidades: EntidadCatalogo[]
  loaded: boolean
  loading: boolean
  setMode: (m: CatalogMode) => void
  load: (token: string | null) => Promise<void>
  reload: (token: string | null) => Promise<void>
  reset: () => void
}

const STORAGE_KEY_MODE = 'arquitecto-catalog-mode'

export const useCatalogStore = create<CatalogState>((set, get) => ({
  mode: (typeof window !== 'undefined' ? (localStorage.getItem(STORAGE_KEY_MODE) as CatalogMode) : null) || 'operativo',
  entidades: [],
  loaded: false,
  loading: false,

  setMode(m) {
    if (typeof window !== 'undefined') localStorage.setItem(STORAGE_KEY_MODE, m)
    set({ mode: m, loaded: false, entidades: [] })
  },

  async load(token: string | null) {
    if (get().loading) return
    if (get().loaded) return
    set({ loading: true })
    try {
      const mode = get().mode
      if (mode === 'operativo') {
        const resp = await api.get<{ data: MisEntidadesItem[] }>('/mis-entidades', token)
        // Adaptar a EntidadCatalogo shape (necesitamos sub/version derivados)
        const entidades: EntidadCatalogo[] = (resp.data || []).map((e) => {
          // El nombre canónico (dominio.sub.token.estado) es la fuente consistente:
          // algunas filas traen token/estado_deseable null en BD (deuda backend) y el
          // dominio llega como "ARP.infra" o "AARPIA.infra". Derivamos del nombre para
          // que el título no salga vacío, el subtítulo sea uniforme y el enlace no se rompa.
          const partes = e.nombre.split('.')
          const subDominio = e.dominio ? e.dominio.replace(/^(ARP|AARPIA)\./, '') : ''
          const sub = subDominio || (partes.length >= 4 ? partes[partes.length - 3] : 'custom')
          const token = e.token || (partes.length >= 2 ? partes[partes.length - 2] : '')
          const estado = e.estado_deseable || (partes.length >= 1 ? partes[partes.length - 1] : '')
          return {
            nombre: e.nombre,
            sub,
            token,
            estado,
            version: e.version || '1.0', // version real del definition; fallback solo si falta (propias custom)
            status: e.status,
            origen: e.origen,
            origen_suscripcion: e.origen_suscripcion,
          }
        })
        set({ entidades, loaded: true, loading: false })
      } else {
        // Vista global: OpenAPI con x-aarpia-entidades (todas con version exacta)
        const spec = await api.get<any>('/docs/openapi.json', token)
        const list = (spec['x-aarpia-entidades'] || []) as EntidadCatalogo[]
        // En modo global, sin info de origen — son todas las del organismo
        const entidades = list.map((e) => ({ ...e, origen: 'catalogo' as EntidadOrigen }))
        set({ entidades, loaded: true, loading: false })
      }
    } catch (e) {
      set({ loading: false })
      throw e
    }
  },

  async reload(token: string | null) {
    set({ loaded: false, entidades: [] })
    await get().load(token)
  },

  reset() {
    set({ entidades: [], loaded: false, loading: false })
  },
}))
